"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Tag, ShoppingBag } from "lucide-react";
import { useSoftCart } from "./SoftCartContext";

interface SoftProductsProps {
  title: string;
  badgeText?: string;
  products: any[];
  subdomain: string;
}

export default function SoftProducts({
  title,
  badgeText = "Verified Stock",
  products,
  subdomain,
}: SoftProductsProps) {
  const { addToCart } = useSoftCart();

  const resolveImg = (url: string | null | undefined, fallback: string) => {
    if (!url) return fallback;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `http://${subdomain}.localhost:8000${cleanPath}`;
  };

  if (!products || products.length === 0) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white rounded-3xl p-12 text-center shadow-[6px_6px_16px_#dce5ef,-6px_-6px_16px_#ffffff] border border-white">
          <Tag className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-xs font-bold text-slate-500">কোনো প্রোডাক্ট পাওয়া যায়নি।</p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="text-[11px] font-black uppercase tracking-wider text-blue-600">
            {badgeText}
          </span>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900">{title}</h2>
        </div>
        <span className="text-xs font-bold text-slate-500 bg-white px-4 py-2 rounded-full shadow-[4px_4px_10px_#dce5ef,-4px_-4px_10px_#ffffff]">
          {products.length} Items Listed
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {products.map((product: any) => {
          const price = Number(product.price || 0);
          const oldPrice = Number(product.old_price || product.regular_price || 0);
          const productImg = resolveImg(
            product.image_url || product.image || product.primary_image,
            "https://images.unsplash.com/photo-1542838132-92c53300491e?w=600"
          );

          return (
            <div
              key={product.id}
              className="bg-white rounded-3xl p-4 shadow-[8px_8px_20px_#dce5ef,-8px_-8px_20px_#ffffff] border border-white/90 hover:-translate-y-1.5 transition-all flex flex-col justify-between group"
            >
              <div>
                <div className="relative w-full h-52 bg-slate-50 rounded-2xl overflow-hidden mb-3.5">
                  <Image
                    src={productImg}
                    alt={product.name}
                    fill
                    unoptimized
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full bg-blue-600 text-white text-[10px] font-black uppercase tracking-wider shadow-md">
                    Verified
                  </span>
                </div>

                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  {product.category?.name || "Marketplace"}
                </span>
                <h3 className="text-sm font-bold text-slate-900 mt-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
                  {product.name}
                </h3>
              </div>

              <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <div className="text-base font-black text-blue-600 font-mono">
                    ৳{price.toLocaleString()}
                  </div>
                  {oldPrice > price && (
                    <span className="text-[11px] text-slate-400 line-through font-mono">
                      ৳{oldPrice.toLocaleString()}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* আলাদা কার্টে অ্যাড করার বাটন */}
                  <button
                    type="button"
                    onClick={() => addToCart(product)}
                    className="w-10 h-10 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center transition-colors shadow-md shadow-blue-500/20 cursor-pointer"
                    title="Add to Cart"
                  >
                    <ShoppingBag className="w-4 h-4" />
                  </button>

                  <Link
                    href={`/products/${product.slug || product.id}`}
                    className="w-10 h-10 rounded-2xl bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white flex items-center justify-center transition-colors shadow-sm cursor-pointer"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}